import { useState } from 'react';
import { findClinicalTrials } from '../api';

const statusColor = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'RECRUITING') return 'bg-green-100 text-green-700';
  if (s === 'COMPLETED') return 'bg-[#fafafa] text-[#4d4d4d]';
  if (s === 'ACTIVE, NOT RECRUITING') return 'bg-amber-50 text-amber-700';
  if (s.includes('TERMINATED') || s.includes('WITHDRAWN')) return 'bg-red-50 text-red-600';
  return 'text-[#808080]';
};

export default function ClinicalTrialsPanel({ initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setTrials([]);
    try {
      const response = await findClinicalTrials(query.trim());
      setTrials(response.data);
      setSearched(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch clinical trials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Clinical Trial Finder</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Find clinical trials related to your research topic from ClinicalTrials.gov.
        </p>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="e.g. CRISPR gene therapy, cancer immunotherapy"
          className="flex-1 rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          disabled={loading}
        />
        <button
          onClick={handleSearch}
          disabled={!query.trim() || loading}
          className="px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                     hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching...
            </>
          ) : 'Search Trials'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {searched && trials.length === 0 && !loading && (
        <p className="text-sm text-[#808080] text-center py-8">No clinical trials found for "{query}".</p>
      )}

      {trials.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-[#808080]">{trials.length} trial{trials.length !== 1 ? 's' : ''} found</p>
          {trials.map((trial) => (
            <div
              key={trial.nct_id}
              className="bg-white rounded-lg p-4"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="text-xs font-mono text-[#808080]">{trial.nct_id}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(trial.status)}`}
                      style={{ boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}>
                      {trial.status || 'Unknown'}
                    </span>
                    {trial.phase && trial.phase !== 'N/A' && (
                      <span className="text-xs text-[#4d4d4d] px-2 py-0.5 rounded-full"
                        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
                        {trial.phase}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-[#171717] leading-snug" style={{ letterSpacing: '-0.32px' }}>{trial.title}</p>

                  {trial.conditions.length > 0 && (
                    <p className="text-xs text-[#808080] mt-1.5">
                      <span className="font-medium text-[#4d4d4d]">Conditions:</span> {trial.conditions.join(', ')}
                    </p>
                  )}
                  {trial.interventions.length > 0 && (
                    <p className="text-xs text-[#808080] mt-0.5">
                      <span className="font-medium text-[#4d4d4d]">Interventions:</span> {trial.interventions.join(', ')}
                    </p>
                  )}
                </div>
                <a
                  href={trial.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-xs text-[#4d4d4d] hover:text-[#171717] font-medium"
                >
                  View →
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
